import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase/config';
import { useAuth } from '@orrekie/hooks/auth/useAuth';
import './BSAQuery.css';
import { BrandedLoader } from '@orrekie/ui';

const BSAQuery = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { currentUser, loading: authLoading } = useAuth();

  // Show loading state if auth context is still loading
  if (authLoading) {
    return <BrandedLoader message="Loading authentication, please wait…" />;
  }

  if (!currentUser) {
    return <div className="bsa-query-container">
      <div className="error-message">You must be logged in to use this tool.</div>
    </div>;
  }

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    try {
      setLoading(true);
      setError('');

      console.debug('Sending BSAQuery request:', {
        query: query.trim()
      });

      // Initialize the cloud function for each call to ensure fresh instance
      const bsaQueryFunction = httpsCallable(functions, 'bsaQueryFunction');
      const result = await bsaQueryFunction({
        query: query.trim()
      });

      console.debug('BSAQuery response:', result);

      if (!result?.data?.success) {
        throw new Error('Query failed: ' + (result?.data?.error || 'Unknown error'));
      }

      const searchResults = result.data.results || [];
      if (!Array.isArray(searchResults)) {
        throw new Error('Invalid response format: expected array of results');
      }

      setResults(searchResults);

      if (searchResults.length === 0) {
        setError('No results found for your query. Please try different search terms.');
      }
    } catch (err) {
      console.error('BSAQuery error:', err);
      const errorMessage = err.message || 'An unexpected error occurred. Please try again.';
      setError(errorMessage.includes('INTERNAL') ? 'Service temporarily unavailable. Please try again later.' : errorMessage);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bsa-query-container">
      <div className="bsa-query-header">
        <h1>Building Safety Act 2022 Query</h1>
        <p>Search through the Building Safety Act 2022 document</p>
      </div>

      <form onSubmit={handleSearch} className="search-form">
        <div className="search-input-container">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Enter your search query..."
            className="search-input"
            disabled={loading}
          />
          <button
            type="submit"
            className="search-button"
            disabled={loading || !query.trim()}
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </form>

      {error && <div className="error-message">{error}</div>}

      <div className="results-container">
        {results.map((result, index) => (
          <div key={index} className="result-card">
            <h3>{result.title}</h3>
            <p className="result-summary">{result.summary}</p>
            {result.documents && (
              <div className="result-meta">
                <span className="source-label">Source Documents:</span>
                <span className="source-documents">{result.documents.join(', ')}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default BSAQuery; 